/**
 * Aurora Customization: This file is necessary to import the custom StatusChangeFields and useInitialValuesForReturnAuth
 */
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import cx from 'classnames';
import { Formik } from 'formik';
import { find, get, isEmpty, isNil } from 'lodash';

import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import Spinner from '@broadleaf/admin-components/dist/common/elements/Spinner/Spinner';
import {
  clearFormikErrors,
  setFormikErrors
} from '@broadleaf/admin-components/dist/form/utils/RequestErrorHelpers';
import {
  getAttribute,
  getEndpointByType
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/components/OrderView/state/actions';
import { ReturnsOrderViewContext } from '@broadleaf/admin-components/dist/oms/components/ReturnsOrderViewSection/contexts';
import * as ReturnAuthUtils from '@broadleaf/admin-components/dist/oms/components/ReturnsOrderViewSection/utils/ReturnAuthUtils';
import {
  IOmsStatusChangeContext,
  StatusChangeContext
} from '@broadleaf/admin-components/dist/oms/contexts';
import { findOrderItem } from '@broadleaf/admin-components/dist/oms/utils/OrderItemUtils';
import FormikError from '@broadleaf/admin-components/dist/form/components/FormikError';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import type { ICollectionStateHook } from '@broadleaf/admin-components/dist/types/collection';
import type { IFormik } from '@broadleaf/admin-components/dist/types/form';
import type { IMetadataAction } from '@broadleaf/admin-components/dist/types/metadata';
import type { IReturnAuthorization } from '@broadleaf/admin-components/dist/types/oms';
import { useContextParams } from '@broadleaf/admin-components/dist/oms/components';

import { useInitialValuesForReturnAuth } from './hooks';
import { hooks } from '@broadleaf/admin-components';
import { StatusChangeFields } from '@broadleaf/admin-components/dist/oms/elements';
import { AuroraStatusChangeFields } from 'app/oms/elements/StatusChangeFields';
const { useEventCallback } = hooks;

export const ReturnAuthStatusChange: React.FC<
  ReturnAuthStatusChangeProps
> = props => {
  const { actionDefinition, returnAuth, returnAuthsState } = props;
  const handleSuccess = useEventCallback(() => {
    // trigger a refresh of the returns collection
    returnAuthsState.handleRead();
  }, [returnAuthsState.handleRead]);
  const context = useReturnAuthStatusChangeContext({ ...props });
  const allowedStatuses = getAttribute(actionDefinition, 'allowedStatuses', []);

  if (
    !isEmpty(allowedStatuses) &&
    !allowedStatuses.includes(returnAuth.status)
  ) {
    // only render if matching one of the allowed statuses
    return null;
  }

  return (
    <StatusChangeContext.Provider value={context}>
      <ReturnAuthStatusChangeAction
        actionDefinition={actionDefinition}
        handleSuccess={handleSuccess}
      />
    </StatusChangeContext.Provider>
  );
};

export interface ReturnAuthStatusChangeProps {
  actionDefinition: IMetadataAction;
  returnAuth: IReturnAuthorization;
  /** The order return authorization {@link CollectionStateHook}. */
  returnAuthsState: ICollectionStateHook;
  isFetching: boolean;
}

interface useReturnAuthStatusChangeContextProps {
  actionDefinition: IMetadataAction;
  returnAuth: IReturnAuthorization;
  isFetching: boolean;
}

export function useReturnAuthStatusChangeContext({
  actionDefinition,
  returnAuth,
  isFetching
}: useReturnAuthStatusChangeContextProps): IOmsStatusChangeContext {
  const { fulfillmentItems, orderDispatch, orderMetadata, orderState } =
    useContext(ReturnsOrderViewContext);
  const contextParams = useContextParams(orderMetadata, orderState, returnAuth);
  const nextStatus = getAttribute(actionDefinition, 'nextStatus');
  const { color } = ReturnAuthUtils.getReturnStatus(orderMetadata, nextStatus);
  const submitEndpoint = getEndpointByType(actionDefinition, 'STATUS_CHANGE');
  const selectAllItems = getAttribute(
    actionDefinition,
    'selectAllItems',
    false
  );
  const selectAllItemsMessage = getAttribute(
    actionDefinition,
    'selectAllItemsMessage',
    false
  );
  return useMemo(
    () => ({
      color,
      contextParams,
      disabled: isFetching,
      fulfillmentItems,
      nextStatus,
      orderDispatch,
      order: orderState.data,
      parent: returnAuth,
      selectAllItems,
      selectAllItemsMessage,
      submitEndpoint
    }),
    [
      color,
      contextParams,
      fulfillmentItems,
      nextStatus,
      orderDispatch,
      orderState.data,
      returnAuth,
      isFetching,
      selectAllItems,
      selectAllItemsMessage,
      submitEndpoint
    ]
  );
}

export interface ReturnAuthStatusChangeActionProps {
  actionDefinition: IMetadataAction;
  handleClose?: Function;
  handleSuccess: Function;
}

export const ReturnAuthStatusChangeAction: React.FC<
  ReturnAuthStatusChangeActionProps
> = ({ actionDefinition, handleClose, handleSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { disabled, orderDispatch } = useContext(StatusChangeContext);
  const statusChangeReasons = getAttribute(
    actionDefinition,
    'statusChangeReasons',
    []
  );
  return (
    <>
      <ReturnAuthStatusChangeButton
        onClick={() => {
          !disabled && setIsOpen(true);
        }}
      >
        {actionDefinition.label}
      </ReturnAuthStatusChangeButton>
      {isOpen && (
        <ReturnAuthStatusChangeModal
          onClose={() => {
            setIsOpen(false);
            !!handleClose && handleClose();
          }}
          onSuccess={response => {
            orderDispatch(queueFetch());
            !!handleSuccess && handleSuccess(response);
          }}
          submitLabel={getAttribute(
            actionDefinition,
            'submitLabel',
            actionDefinition.label
          )}
          title={actionDefinition.label}
          statusChangeReasons={statusChangeReasons}
        />
      )}
    </>
  );
};

export interface ReturnAuthStatusChangeModalProps {
  onClose: () => void;
  onSuccess: (response: any) => void;
  submitLabel: ReactNode;
  title: string;
  statusChangeReasons?: string[];
}

export const ReturnAuthStatusChangeModal: FC<
  ReturnAuthStatusChangeModalProps
> = ({ onClose, onSuccess, submitLabel, title, statusChangeReasons }) => {
  const { contextParams, fulfillmentItems, order, parent, submitEndpoint } =
    useContext(StatusChangeContext);
  const returnAuth = parent as IReturnAuthorization;
  const initialValues = useInitialValuesForReturnAuth(); // sets initial PO number
  const handleSubmit = useCallback(
    async (values, formik) => {
      formik.setSubmitting(true);
      try {
        values.items.forEach(item => {
          item.overrideTotalToRefund = isNil(item.overrideTotalToRefund)
            ? undefined
            : {
                amount: item.overrideTotalToRefund,
                currency: returnAuth.currency
              };
        });

        const response = await request(
          { method: 'post', ...submitEndpoint, data: values },
          contextParams
        );
        clearFormikErrors(formik);
        onSuccess(response);
        onClose();
      } catch (err) {
        setFormikErrors(err, formik);
      } finally {
        formik.setSubmitting(false);
      }
    },
    [returnAuth.currency, contextParams, submitEndpoint, onClose, onSuccess]
  );
  const renderItem = useCallback(
    formik => (item, i) => {
      const rootPath = `items[${i}]`;
      const returnItem = find(returnAuth.items, {
        orderFulfillmentItemId: item.orderFulfillmentItemId
      });
      const quantityChangingStatusName = `${rootPath}.quantity`;
      const overrideTotalToRefundName = `${rootPath}.overrideTotalToRefund`;
      const reasonName = `${rootPath}.reason`;

      const fulfillmentItem = find(fulfillmentItems, {
        id: returnItem.orderFulfillmentItemId
      });
      const orderItem = findOrderItem(order.orderItems, {
        id: fulfillmentItem.orderItemId
      });
      const pendingOrConfirmedPredicate = confirmation =>
        confirmation.status === 'PENDING' ||
        confirmation.status === 'COMPLETED';

      const quantityPendingOrConfirmed = returnItem.confirmations
        .filter(pendingOrConfirmedPredicate)
        .map(confirmation => confirmation.quantity)
        .reduce((sum, confirmed) => sum + confirmed, 0);
      const confirmableQuantity =
        returnItem.quantity - quantityPendingOrConfirmed;

      const estimatedTotalPendingOrRefunded = returnItem.confirmations
        .filter(pendingOrConfirmedPredicate)
        .map(confirmation =>
          isNil(confirmation.overrideTotalToRefund)
            ? confirmation.totalToRefund
            : confirmation.overrideTotalToRefund
        )
        .reduce((sum, refunded) => sum + refunded.amount, 0);
      const refundableAmount =
        returnItem.estimatedTotalToRefund.amount -
        estimatedTotalPendingOrRefunded;
      const refundableTotal = {
        amount: refundableAmount,
        currency: returnItem.estimatedTotalToRefund.currency
      };

      return (
        <React.Fragment key={i}>
          <StatusChangeFields.Quantity
            formik={formik}
            quantityChangingStatusName={quantityChangingStatusName}
            orderItem={orderItem}
            itemQuantity={confirmableQuantity}
            unitMerchandisePrice={orderItem.unitPrice}
            quantityChangingStatusValue={item.quantity}
            statusChangeReasons={statusChangeReasons}
            reasonChangingStatusName={reasonName}
          />

          <div className="tw-mt-4">
            <StatusChangeFields.EstimatedTotalToRefund
              estimatedTotalToRefund={refundableTotal}
            />
            <StatusChangeFields.OverrideTotalToRefund
              formik={formik}
              overrideTotalToRefundName={overrideTotalToRefundName}
              overrideTotalToRefundValue={item.overrideTotalToRefund}
              overrideTotalToRefundMax={
                returnAuth.estimatedTotalToRefund.amount
              }
            />
          </div>
        </React.Fragment>
      );
    },
    [fulfillmentItems, order, returnAuth]
  );
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {formik => (
        <SimpleModal
          closeOnClickOutside={false}
          footer={
            <ReturnAuthStatusChangeModalFooter
              formik={formik}
              submitLabel={submitLabel}
            />
          }
          isOpen
          onClose={onClose}
          size="lg"
          title={title}
        >
          <AuroraStatusChangeFields
            formik={formik}
            renderItem={renderItem}
            paymentGatewayType={get(order, 'paymentGatewayType')}
            fleetTaxExempt={get(order, 'attributes.FLEET_TAX_EXEMPT', false)}
          />
        </SimpleModal>
      )}
    </Formik>
  );
};

export interface ReturnAuthStatusChangeModalFooterProps {
  formik: IFormik;
  submitLabel: ReactNode;
}

export const ReturnAuthStatusChangeModalFooter: FC<
  ReturnAuthStatusChangeModalFooterProps
> = ({ formik, submitLabel }) => {
  return (
    <>
      {formik.isSubmitting ? (
        <Spinner className="tw-mr-2" color="info" />
      ) : (
        <FormikError className="tw-mr-2" formik={formik} />
      )}

      <button
        className="tw-w-full tw-rounded tw-border tw-bg-primary-500 tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-primary-100 tw-shadow hover:tw-bg-primary-600 focus:tw-outline-none focus:tw-ring md:tw-w-auto md:tw-py-2"
        onClick={() => formik.submitForm()}
        type="submit"
      >
        {submitLabel}
      </button>
    </>
  );
};

export interface ReturnAuthStatusChangeButtonProps {
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
}

export const ReturnAuthStatusChangeButton: React.FC<
  ReturnAuthStatusChangeButtonProps
> = ({ children, disabled, onClick }) => {
  const { color } = useContext(StatusChangeContext);
  return (
    <button
      className={cx(
        'tw-w-full tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-shadow',
        'focus:tw-outline-none focus:tw-ring disabled:tw-cursor-not-allowed',
        'md:tw-py-2',
        `tw-text-${color}-700 hover:tw-bg-${color}-100 focus:tw-bg-${color}-100 active:tw-bg-${color}-200`
      )}
      disabled={disabled}
      onClick={!disabled && onClick}
      type="button"
    >
      {children}
    </button>
  );
};

export default ReturnAuthStatusChange;
